import type { AuthSession } from '@secretario/shared';
import { useSyncExternalStore } from 'react';

const SESSION_KEY = 'secretario.session';
const DEVICE_KEY = 'secretario.device';
const KNOWN_ACCOUNT_KEY = 'secretario.knownAccount';

export type StoredSession = AuthSession;

type Listener = () => void;
const listeners = new Set<Listener>();
let cached: StoredSession | null | undefined;

function readStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: unknown) {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    /* armazenamento indisponível: a sessão vive só em memória */
  }
}

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

export function readSession(): StoredSession | null {
  if (cached === undefined) {
    cached = readStorage<StoredSession>(SESSION_KEY);
  }
  return cached;
}

export function writeSession(session: StoredSession | null) {
  cached = session;
  writeStorage(SESSION_KEY, session);
  if (session) {
    writeStorage(KNOWN_ACCOUNT_KEY, true);
  }
  emit();
}

export function clearSession() {
  writeSession(null);
  void import('./profileCache.js').then((cache) => cache.clearCachedProfile()).catch(() => {});
  void import('./notify.js').then((notify) => notify.clearAllNotices()).catch(() => {});
  // A loja do aparelho esquece a conta que saiu (só faz algo se ela chegou a ser ligada).
  void import('./storeBilling.js').then((store) => store.storeLogOut()).catch(() => {});
}

/** Este aparelho já entrou numa conta antes? Decide se a entrada começa por "entrar" ou "criar". */
export function hasKnownAccount(): boolean {
  return readStorage<boolean>(KNOWN_ACCOUNT_KEY) === true;
}

export type DeviceRegistration = {
  publicId: string;
  platform: 'android' | 'ios' | 'web';
  label: string;
};

function detectPlatform(): DeviceRegistration['platform'] {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'web';
}

function deviceLabel(platform: DeviceRegistration['platform']): string {
  if (platform === 'android') return 'Android';
  if (platform === 'ios') return 'iPhone';
  return 'Navegador';
}

/** Identidade estável deste aparelho, criada uma vez e reaproveitada em cada entrada. */
export function getDevice(): DeviceRegistration {
  const existing = readStorage<DeviceRegistration>(DEVICE_KEY);
  if (existing?.publicId) {
    return existing;
  }
  const platform = detectPlatform();
  const created: DeviceRegistration = {
    publicId: crypto.randomUUID(),
    platform,
    label: deviceLabel(platform),
  };
  writeStorage(DEVICE_KEY, created);
  return created;
}

/**
 * Identidade nova para este aparelho. O servidor não passa um aparelho de uma conta para
 * outra ("Dispositivo indisponível"): quem troca de conta no mesmo app (sair e entrar com
 * outro número, ou outra pessoa no mesmo telefone) entra com uma identidade nova.
 */
export function renewDevice(): DeviceRegistration {
  const platform = detectPlatform();
  const created: DeviceRegistration = {
    publicId: crypto.randomUUID(),
    platform,
    label: deviceLabel(platform),
  };
  writeStorage(DEVICE_KEY, created);
  return created;
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): StoredSession | null {
  return useSyncExternalStore(subscribe, readSession, () => null);
}
